import { useCallback, useEffect, useRef, useState } from 'react';

let scriptPromise = null;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Captcha failed to load.'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Renders a Turnstile widget and hands out single-use tokens.
 * An empty site key means captcha is off and acquireToken resolves to ''.
 */
export function useTurnstile(siteKey) {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const unused = useRef('');
  const pending = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    let renderedId = null;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || !turnstile) return;
        renderedId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (value) => {
            if (pending.current) {
              const waiting = pending.current;
              pending.current = null;
              waiting.resolve(value);
              return;
            }
            unused.current = value;
          },
          'expired-callback': () => {
            unused.current = '';
          },
          'error-callback': () => {
            unused.current = '';
            setError('Captcha failed to load.');
            if (pending.current) {
              const waiting = pending.current;
              pending.current = null;
              waiting.reject(new Error('Complete the captcha and try again.'));
            }
          },
        });
        widgetId.current = renderedId;
      })
      .catch(() => {
        if (!cancelled) setError('Captcha failed to load.');
      });
    return () => {
      cancelled = true;
      if (renderedId != null && window.turnstile) window.turnstile.remove(renderedId);
      widgetId.current = null;
    };
  }, [siteKey]);

  const acquireToken = useCallback(() => {
    if (!siteKey) return Promise.resolve('');
    if (unused.current) {
      const value = unused.current;
      unused.current = '';
      if (widgetId.current != null && window.turnstile) window.turnstile.reset(widgetId.current);
      return Promise.resolve(value);
    }
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        if (pending.current) {
          pending.current = null;
          reject(new Error('Complete the captcha and try again.'));
        }
      }, 60000);
      pending.current = {
        resolve: (value) => {
          window.clearTimeout(timer);
          if (widgetId.current != null && window.turnstile) window.turnstile.reset(widgetId.current);
          resolve(value);
        },
        reject: (err) => {
          window.clearTimeout(timer);
          reject(err);
        },
      };
    });
  }, [siteKey]);

  return { containerRef, acquireToken, error, active: Boolean(siteKey) };
}
